import {
    Component,
    OnDestroy,
    Renderer2,
    OnInit,
    ElementRef,
    ViewChild,
    Input,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as UserActions from '../../store/user/user.actions';
import { IAppState } from '../../store/interface/app.state.interface';
import { UserService } from '../../user/services/user.service';
import { AuthService } from '../../auth/services/auth.service';
import { IUser } from '../../user/interfaces/user.interface';
import { Subscription } from 'rxjs';
import { LayoutService } from '../services/layout.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { TenantActionType } from '../../tenant/enum/tenant-action-type.enum';
import { TenantService } from '../../tenant/services/tenant.service';
import { ConfirmationType } from '../enum/confirmation-type.enum';
import { openWebsite } from '../../shared/utils/open-tenant-website.util';
import * as DashboardActions from '../../store/tenant/dashboard.action';
import * as SettingsActions from '../../store/settings/settings.actions';
import { UserTypes } from '../../shared/enum/user-types.enum';

@Component({
    standalone: false,
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
    @Input() showMenuIcon = true;
    @ViewChild('confirmationModal') confirmationModal!: ConfirmationModalComponent;
    loggedInUser: IUser | null = null;
    isOpen = false;
    isPaletteOpen = false;
    subscriptions: Subscription[] = [];
    isMenuOpen = false;
    isUserOpen = false;
    selectedId: string | null = null;
    confirmationType: ConfirmationType | null = null;
    tenantActionType: typeof TenantActionType = TenantActionType;
    confirmationTitle = '';
    confirmationMessage = '';
    private documentClickListener!: () => void;
    isPublishingTenant = false;

    constructor(
        private messageService: MessageService,
        private router: Router,
        private store: Store<IAppState>,
        private userService: UserService,
        public layoutService: LayoutService,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private tenantService: TenantService,
        private authService: AuthService,
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.store
                .select((state) => state.user.loggedInUser)
                .subscribe((state) => {
                    this.loggedInUser = state;
                    // ========================================================================================
                    // ------TODO: Firebase workflow status check temporarily disabled under ticket #511.--------
                    // ========================================================================================
                    // this.fcmService.checkWorkflowStatusFromUser(this.loggedInUser);
                }),
        );

        this.subscriptions.push(
            this.store
                .select((state) => state.user.publishingTenantId)
                .subscribe((publishingTenantId) => {
                    if (
                        publishingTenantId &&
                        publishingTenantId === this.loggedInUser?.tenant?.id
                    ) {
                        this.isPublishingTenant = true;
                    } else {
                        this.isPublishingTenant = false;
                    }
                }),
        );

        this.documentClickListener = this.renderer.listen(
            'document',
            'click',
            (event: MouseEvent) => {
                this.handleOutsideClick(event);
            },
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        if (this.documentClickListener) {
            this.documentClickListener();
        }
    }

    onToggleTheme(): void {
        this.layoutService.toggleTheme();
    }

    get themeIcon(): string {
        return this.layoutService.isDarkTheme() ? 'pi pi-sun' : 'pi pi-moon';
    }

    get themeTooltip(): string {
        return this.layoutService.isDarkTheme() ? 'Switch to Light Mode' : 'Switch to Dark Mode';
    }

    onPublishTenant(id: string): void {
        this.selectedId = id;
        this.confirmationType = ConfirmationType.PUBLISH_WEBSITE;
        this.confirmationTitle = 'Publish Website';
        this.confirmationMessage = 'Are you sure you want to publish the Website?';
        this.confirmationModal.open();
    }

    onVisitTenantWebsite(): void {
        openWebsite(
            this.loggedInUser?.tenant?.websiteDomainUrl ||
                this.loggedInUser?.tenant?.platformDomainUrl,
        );
    }

    onConfirmed(result: boolean): void {
        if (!result) {
            this.selectedId = null;
            this.confirmationType = null;
            return;
        }
    }

    handleOutsideClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        const clickedInside = this.elementRef.nativeElement.contains(target);
        if (clickedInside) return;
        this.isOpen = false;

        if (!target.closest('.layout-topbar-ellipsis-container')) {
            this.isMenuOpen = false;
            this.isUserOpen = false;
        }
    }

    logout(): void {
        this.store.dispatch(UserActions.setLoggedInUser({ user: null }));
        this.authService.logout().finally(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Logged out',
                detail: 'You have been successfully logged out.',
            });
            localStorage.removeItem('auth_token');
            this.router.navigate(['/auth/login']);
        });
    }

    /*=====================================
      Whether this account belongs to the platform rather than to a store.

      Exit Tenant switches back to Seldit, and only a TT_USER holds a
      membership there - a merchant clicking it is refused with a 404. The
      server derives accountType from userType (anything that is not a
      customer is a management account), so ttUser already means a management
      account and there is no second condition to check.
      @return whether the platform-only actions belong to this account
    ======================================*/
    get isTTUserAccount(): boolean {
        return this.loggedInUser?.userType?.key === UserTypes.TT_USER;
    }

    exitTenant(): void {
        if (!this.loggedInUser) return;

        this.authService
            .switchTenant({ membershipId: null })
            .then(({ token }) => {
                localStorage.setItem('auth_token', token);
                return this.userService.fetchLoggedInUser();
            })
            .then(() => this.tenantService.fetchTenantStatsCount())
            .then((data) => {
                this.store.dispatch(
                    DashboardActions.setTenantStatsCount({
                        counts: data,
                    }),
                );

                this.store.dispatch(
                    SettingsActions.setSettings({
                        settings: null,
                    }),
                );

                this.messageService.add({
                    severity: 'success',
                    summary: 'Tenant Exit',
                    detail: 'You have successfully exited the tenant.',
                });

                this.router.navigate(['/tenant']);
            });
    }

    manageProfile(): void {
        this.router.navigate([this.loggedInUser ? '/user/manage-profile' : '/']);
    }

    manageAccount(): void {
        this.router.navigate([this.loggedInUser?.tenant?.id ? '/tenant/manage-account' : '/']);
    }
}
